// Аплоадер: дренирует IndexedDB на сервер с экспоненциальным backoff.
// Идемпотентный PUT — повтор безопасен. Живёт независимо от WebRTC.

import { VERIFIED_TAIL_KEEP } from '@besedka/shared';
import { countLocal, getLocalChunks, markVerified, pruneVerified } from './idb.js';

export interface UploaderStats {
  pendingChunks: number;
  uploadLagMs: number;
  lastError: string | null;
}

type StatsListener = (stats: UploaderStats) => void;

// Молча зависший TCP-коннект (Wi-Fi умер без RST) не должен блокировать
// очередь: PUT убивается по таймауту и уходит в ретрай
const UPLOAD_TIMEOUT_MS = 90_000;

class Uploader {
  private running = false;
  private backoffMs = 1000;
  private wakeUp: (() => void) | null = null;
  private listeners = new Set<StatsListener>();
  stats: UploaderStats = { pendingChunks: 0, uploadLagMs: 0, lastError: null };

  constructor() {
    // Сеть вернулась — не ждём хвост backoff-паузы, продолжаем сразу
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this.backoffMs = 1000;
        this.wakeUp?.();
        this.kick();
      });
    }
  }

  onStats(listener: StatsListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private async refreshStats(lastError: string | null = this.stats.lastError): Promise<void> {
    const { count, oldestAgeMs } = await countLocal();
    this.stats = { pendingChunks: count, uploadLagMs: oldestAgeMs, lastError };
    for (const listener of this.listeners) listener(this.stats);
  }

  /** Пнуть аплоадер (после записи нового чанка или при старте страницы). */
  kick(): void {
    if (this.running) return;
    this.running = true;
    void this.loop().finally(() => {
      this.running = false;
    });
  }

  /** Прерываемая backoff-пауза: событие online будит раньше срока. */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.wakeUp = null;
        resolve();
      }, ms);
      this.wakeUp = () => {
        clearTimeout(timer);
        this.wakeUp = null;
        resolve();
      };
    });
  }

  private async putChunkWithTimeout(chunk: { episodeId: number; segmentId: number; seq: number; blob: Blob }) {
    const controller = new AbortController();
    const timer = setTimeout(
      () => controller.abort(new Error(`upload timed out after ${UPLOAD_TIMEOUT_MS / 1000}s`)),
      UPLOAD_TIMEOUT_MS
    );
    try {
      return await fetch(`/api/episodes/${chunk.episodeId}/chunks/${chunk.segmentId}/${chunk.seq}`, {
        method: 'PUT',
        headers: { 'content-type': 'application/octet-stream' },
        body: chunk.blob,
        signal: controller.signal
      });
    } finally {
      clearTimeout(timer);
    }
  }

  private async loop(): Promise<void> {
    for (;;) {
      await this.refreshStats();
      const local = await getLocalChunks();
      const chunk = local[0];
      if (!chunk) return;
      try {
        const response = await this.putChunkWithTimeout(chunk);
        if (response.status === 409 || response.status === 404 || response.status === 403) {
          // Терминально для этого чанка (выпуск собран / сегмент чужой или удалён):
          // помечаем verified, чтобы не блокировать очередь свежих чанков
          await markVerified(chunk.key);
          await this.refreshStats('Часть старых чанков уже не нужна серверу (выпуск собран)');
          continue;
        }
        if (!response.ok) throw new Error(`upload failed: HTTP ${response.status}`);
        await markVerified(chunk.key);
        await pruneVerified(chunk.segmentId, VERIFIED_TAIL_KEEP);
        this.backoffMs = 1000;
        await this.refreshStats(null);
      } catch (error) {
        await this.refreshStats((error as Error).message);
        await this.sleep(this.backoffMs);
        this.backoffMs = Math.min(this.backoffMs * 2, 30_000);
      }
    }
  }
}

export const uploader = new Uploader();
